import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { APP } from '../config/app';

export default function SetupPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('validating'); // validating | ready | submitting | done | error
  const [tokenUser, setTokenUser] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setTokenError('No setup token found. Check the link in your email.');
      return;
    }

    authAPI.validateSetupToken(token)
      .then(data => {
        if (data.valid) {
          setTokenUser(data.user);
          setStatus('ready');
        } else {
          setStatus('error');
          setTokenError(data.error || 'This setup link is invalid or has expired.');
        }
      })
      .catch(() => {
        setStatus('error');
        setTokenError('Could not validate setup link. Please try again.');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    try {
      const data = await authAPI.setupPassword(token, password);
      if (data.success) {
        setStatus('done');
      } else {
        setSubmitError(data.error || 'Failed to set password.');
        setStatus('ready');
      }
    } catch (err) {
      setSubmitError(err.message || 'Your password could not be set. The link may have expired, so ask the equipment desk for a new invite.');
      setStatus('ready');
    }
  };

  return (
    <div className="setup-password-page">
      <div className="setup-password-card">
        <img
          src={`${APP.basePath}/ncadLogo.gif`}
          alt="NCAD"
          className="setup-password-logo"
        />
        <h1 className="setup-password-title">Set Your Password</h1>

        {status === 'validating' && (
          <p className="setup-password-message">Verifying your setup link...</p>
        )}

        {status === 'error' && (
          <div className="setup-password-error-block">
            <p>{tokenError}</p>
            <a href={`${APP.basePath}/`} className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
              Back to Login
            </a>
          </div>
        )}

        {(status === 'ready' || status === 'submitting') && tokenUser && (
          <>
            <p className="setup-password-welcome">
              Welcome, <strong>{tokenUser.full_name}</strong>.<br />
              Choose a password for <strong>{tokenUser.email}</strong>.
            </p>
            <form className="setup-password-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="sp-password">New Password</label>
                <input
                  id="sp-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="At least 8 characters"
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="sp-confirm">Confirm Password</label>
                <input
                  id="sp-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="form-input"
                  placeholder="Re-enter your password"
                  required
                />
              </div>
              {submitError && (
                <p className="setup-password-field-error">{submitError}</p>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={status === 'submitting'}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                {status === 'submitting' ? 'Setting password...' : 'Set Password & Log In'}
              </button>
            </form>
          </>
        )}

        {status === 'done' && (
          <div className="setup-password-done">
            <p>Your password has been set successfully.</p>
            <a href={`${APP.basePath}/`} className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
              Go to Login
            </a>
          </div>
        )}
      </div>

      <style>{`
        .setup-password-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--surface-page);
          padding: 2rem;
        }
        .setup-password-card {
          background: var(--surface-base);
          border-radius: 12px;
          box-shadow: var(--shadow-md);
          padding: 2.5rem 2rem;
          width: 100%;
          max-width: 420px;
          text-align: center;
        }
        .setup-password-logo {
          height: 56px;
          margin-bottom: 1.5rem;
        }
        .setup-password-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 1rem;
        }
        .setup-password-welcome {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }
        .setup-password-message {
          color: var(--text-tertiary);
        }
        .setup-password-form {
          text-align: left;
        }
        .setup-password-form .form-group {
          margin-bottom: 1rem;
        }
        .setup-password-form label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.35rem;
          font-size: 0.875rem;
        }
        .setup-password-field-error {
          color: var(--color-error);
          font-size: 0.875rem;
          margin: 0.25rem 0 0.5rem;
        }
        .setup-password-error-block {
          color: var(--text-primary);
        }
        .setup-password-done {
          color: var(--color-success);
        }
      `}</style>
    </div>
  );
}
